import { Recipient } from "./Recipient";
import { RecipientUpdate } from "./RecipientUpdate";

export type RecipientUser = {
  id: string;
  upn: string;
  isToDelete?: boolean;
};

export type RecipientOrg = {
  organisationRecipientId: string;
  id: string;
  name: string;
  users: RecipientUser[];
  isSelected?: boolean;
  isToDelete?: boolean;
  assignedToUpn: string | null;
  assignedToDisplayName: string | null;
  assignedToUpnAO: string | null;
  assignedToDisplayNameAO: string | null;
  closed: boolean;
  status: string | null;
};

export type RecipientPotentiallyUnknownOrg = Omit<
  RecipientOrg,
  "organisationRecipientId"
> & {
  organisationRecipientId: string | null;
};

export type RecipientsViewModel<TOrg = RecipientOrg> = {
  id: string;
  orgs: TOrg[];
  roles: string[];
};

export const toViewModel = (id: string, model: Recipient[]) => {
  const result: RecipientsViewModel = {
    id,
    orgs: [],
    roles: [],
  };

  for (const item of model) {
    let org = result.orgs.find((org) => org.id === item.organisationId);
    if (!org) {
      org = {
        organisationRecipientId: item.organisationRecipientId,
        id: item.organisationId,
        name: item.organisationName,
        users: [],
        assignedToUpn: item.assignedToUPN,
        assignedToUpnAO: item.assignedToUPNAO,
        assignedToDisplayName: item.assignedToDisplayName,
        assignedToDisplayNameAO: item.assignedToDisplayNameAO,
        closed: item.closed,
        status: item.status,
      };
      result.orgs.push(org);
    }

    const newRole = item.role;
    if (newRole) {
      const isRoleNew = !result.roles.includes(newRole);
      if (isRoleNew) {
        result.roles.push(newRole);
      }
    }

    const newUserId = item.userId;
    if (newUserId) {
      const user = org.users.find((user) => user.id === newUserId);
      if (!user) {
        org.users.push({ id: newUserId, upn: item.upn! });
      }
    }
  }

  return result;
};

export const fromViewModel = (
  model: Recipient[],
  newViewModel: RecipientsViewModel<RecipientPotentiallyUnknownOrg>
) => {
  const result: RecipientUpdate[] = [];

  for (const org of newViewModel.orgs) {
    for (const role of newViewModel.roles) {
      result.push({
        id:
          // if this is an existing record, reattach the id
          model.find(
            (item) => item.organisationId === org.id && item.role === role
          )?.id,
        informationRequestId: newViewModel.id,
        organisationId: org.id,
        role,
        userId: null,
      });
    }

    for (const user of org.users) {
      result.push({
        id:
          // if this is an existing record, reattach the id
          model.find(
            (item) => item.organisationId === org.id && item.userId === user.id
          )?.id,
        informationRequestId: newViewModel.id,
        organisationId: org.id,
        role: null,
        userId: user.id,
      });
    }
  }

  return result;
};

export const toPotentiallyUnknownOrgViewModel = (
  viewModel: RecipientsViewModel
) => viewModel as RecipientsViewModel<RecipientPotentiallyUnknownOrg>;
